import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';

const G = '#00C060';
const G_DARK = '#006633';
const BG = '#f0f7f2';
const CARD = '#ffffff';
const BORDER = 'rgba(0, 192, 96, 0.4)';
const SHADOW = '0 2px 12px rgba(0,0,0,0.08)';
const inputStyle = { background: '#f8faf8', borderColor: 'rgba(0,166,81,0.35)', color: '#1a3322' };

const NavBar = ({ user, navigate }) => (
  <nav className="navbar navbar-dark sticky-top" style={{ background: '#0a1f0c', padding: '10px 0', borderBottom: `2px solid rgba(0,192,96,0.4)` }}>
    <div className="container d-flex justify-content-between align-items-center">
      <div className="d-flex align-items-center gap-3">
        <img src="/logo-white.png" alt="RNP" style={{ width: '46px', height: 'auto' }} />
        <div>
          <div className="fw-bold text-white" style={{ fontSize: '16px' }}>Reaktor Nature Power</div>
          <div style={{ fontSize: '11px', color: G }}>Gestion des Sessions</div>
        </div>
      </div>
      <div className="d-flex align-items-center gap-3">
        <span className="text-white d-none d-md-block" style={{ fontSize: '13px' }}>
          <i className="fas fa-user-shield me-1" style={{ color: G }}></i>{user?.username}
        </span>
        <button onClick={() => navigate('/dashboard')} className="btn btn-sm"
          style={{ background: 'rgba(0,192,96,0.2)', border: `1px solid rgba(0,192,96,0.5)`, color: '#fff', fontSize: '13px' }}>
          <i className="fas fa-arrow-left me-2"></i>Dashboard
        </button>
      </div>
    </div>
  </nav>
);

const Sessions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') navigate('/dashboard');
    else loadSessions();
  }, [user, navigate]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/sessions', { params: { brand: BRAND } });
      setSessions(response.data);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des sessions' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (sessionId) => {
    const session = sessions.find(s => s.id === sessionId);
    const newStatus = session.status === 'active' ? 'pending' : 'active';
    try {
      if (newStatus === 'active') {
        await api.put(`/admin/sessions/${sessionId}/activate`, {}, { params: { brand: BRAND } });
      } else {
        await api.put(`/admin/sessions/${sessionId}/deactivate`, {}, { params: { brand: BRAND } });
      }
      setSessions(sessions.map(s => s.id === sessionId ? { ...s, status: newStatus } : s));
      setMessage({ type: 'success', text: newStatus === 'active' ? 'Session activée' : 'Session désactivée' });
    } catch (error) {
      setMessage({ type: 'danger', text: 'Erreur lors du changement de statut' });
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Supprimer cette session ?')) return;
    try {
      await api.delete(`/admin/sessions/${sessionId}`, { params: { brand: BRAND } });
      setSessions(sessions.filter(s => s.id !== sessionId));
      setMessage({ type: 'success', text: 'Session supprimée' });
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  const statusBadge = (status) => {
    const cfg = {
      active: { label: 'En cours', color: '#00A651', bg: 'rgba(0,192,96,0.15)', border: 'rgba(0,192,96,0.4)' },
      pending: { label: 'Planifiée', color: '#f57c00', bg: 'rgba(245,124,0,0.15)', border: 'rgba(245,124,0,0.4)' },
      completed: { label: 'Terminée', color: 'rgba(0,0,0,0.45)', bg: 'rgba(255,255,255,0.08)', border: 'rgba(255,255,255,0.2)' },
      cancelled: { label: 'Annulée', color: '#e53935', bg: 'rgba(229,57,53,0.15)', border: 'rgba(229,57,53,0.4)' }
    }[status] || { label: status, color: 'rgba(0,0,0,0.45)', bg: 'transparent', border: 'rgba(255,255,255,0.2)' };
    return <span style={{ padding: '4px 12px', borderRadius: '20px', background: cfg.bg, color: cfg.color, fontSize: '12px', fontWeight: 700, border: `1px solid ${cfg.border}` }}>{cfg.label}</span>;
  };

  if (loading) return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: BG }}>
      <div className="spinner-border" style={{ color: G }} role="status"></div>
    </div>
  );

  return (
    <div className="min-vh-100" style={{ background: BG }}>
      <NavBar user={user} navigate={navigate} />
      <div className="container py-4">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
            {message.text}
            <button type="button" className="btn-close btn-close-white" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="fw-bold mb-1" style={{ color: '#1a3322', fontSize: '26px' }}>
              <i className="fas fa-calendar-alt me-2" style={{ color: G }}></i>Sessions
            </h2>
            <p style={{ color: 'rgba(0,0,0,0.5)', margin: 0 }}>{sessions.length} session(s)</p>
          </div>
          <button onClick={() => { setSelectedSession(null); setShowModal(true); }}
            className="btn fw-bold" style={{ background: `linear-gradient(135deg, ${G}, ${G_DARK})`, color: '#fff', border: 'none', padding: '10px 22px', borderRadius: '10px', boxShadow: '0 4px 14px rgba(0,192,96,0.35)' }}>
            <i className="fas fa-plus me-2"></i>Nouvelle Session
          </button>
        </div>

        <div className="row g-3 mb-4">
          {[
            { label: 'Total', value: sessions.length, icon: 'fa-calendar-check', color: G },
            { label: 'Actives', value: sessions.filter(s => s.status === 'active').length, icon: 'fa-play-circle', color: '#00A651' },
            { label: 'Planifiées', value: sessions.filter(s => s.status === 'pending').length, icon: 'fa-clock', color: '#f57c00' },
            { label: 'Terminées', value: sessions.filter(s => s.status === 'completed').length, icon: 'fa-check-circle', color: 'rgba(0,0,0,0.45)' },
          ].map(k => (
            <div key={k.label} className="col-6 col-md-3">
              <div style={{ background: CARD, borderRadius: '14px', padding: '20px', border: `1px solid ${BORDER}`, boxShadow: SHADOW }}>
                <div style={{ width: '44px', height: '44px', background: k.color + '22', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                  <i className={`fas ${k.icon}`} style={{ color: k.color, fontSize: '20px' }}></i>
                </div>
                <div style={{ fontSize: '30px', fontWeight: 800, color: '#1a3322' }}>{k.value}</div>
                <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{k.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: CARD, borderRadius: '16px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, overflow: 'hidden' }}>
          <div style={{ padding: '18px 24px', borderBottom: `1px solid rgba(0,192,96,0.2)`, background: 'rgba(0,0,0,0.2)' }}>
            <h5 style={{ color: '#1a3322', margin: 0, fontWeight: 700 }}>Liste des Sessions ({sessions.length})</h5>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #00C060 0%, #00A651 100%)' }}>
                  {['Point de Vente', 'Session', 'Date & Horaire', 'Statut', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '14px 16px', color: '#1a3322', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: h === 'Actions' || h === 'Statut' ? 'center' : 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr><td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: 'rgba(0,0,0,0.35)' }}>
                    <i className="fas fa-calendar-times fa-3x mb-3 d-block" style={{ opacity: 0.3, color: G }}></i>
                    Aucune session
                  </td></tr>
                ) : sessions.map((session, idx) => (
                  <tr key={session.id} style={{ borderBottom: `1px solid rgba(0,192,96,0.15)`, background: idx % 2 === 0 ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.1)' }}>
                    <td style={{ padding: '14px 16px', color: '#1a3322', fontWeight: 600 }}>{session.pdv_name || 'N/A'}</td>
                    <td style={{ padding: '14px 16px', color: '#2a5a3a' }}>{session.name}</td>
                    <td style={{ padding: '14px 16px', color: 'rgba(0,0,0,0.55)', fontSize: '13px' }}>
                      {new Date(session.start_time).toLocaleDateString('fr-FR')} {new Date(session.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} — {new Date(session.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>{statusBadge(session.status)}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <div className="d-flex gap-2 justify-content-center">
                        <button onClick={() => handleToggle(session.id)} style={{ padding: '5px 12px', background: session.status === 'active' ? 'rgba(0,192,96,0.15)' : 'rgba(229,57,53,0.1)', color: session.status === 'active' ? '#00A651' : '#e53935', border: `1px solid ${session.status === 'active' ? 'rgba(0,192,96,0.4)' : 'rgba(229,57,53,0.4)'}`, borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                          <i className={`fas fa-${session.status === 'active' ? 'toggle-on' : 'toggle-off'}`}></i>
                        </button>
                        <button onClick={() => { setSelectedSession(session); setShowModal(true); }} style={{ padding: '5px 12px', background: 'rgba(24,144,255,0.15)', color: '#1890ff', border: '1px solid rgba(24,144,255,0.4)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button onClick={() => handleDelete(session.id)} style={{ padding: '5px 12px', background: 'rgba(229,57,53,0.1)', color: '#e53935', border: '1px solid rgba(229,57,53,0.4)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <SessionModal session={selectedSession} onClose={() => setShowModal(false)} onSave={async () => { setShowModal(false); await loadSessions(); }} />
      )}
    </div>
  );
};

const SessionModal = ({ session, onClose, onSave }) => {
  const [pdvs, setPdvs] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [formData, setFormData] = useState({
    name: session?.name || '',
    pdv_id: session?.pdv_id || '',
    start_time: session?.start_time ? new Date(session.start_time).toISOString().slice(0, 16) : '',
    end_time: session?.end_time ? new Date(session.end_time).toISOString().slice(0, 16) : '',
    gain_mode: session?.gain_mode || 'auto',
    ratio_value: session?.ratio_value || 5,
    stock_allocations: session?.stock_allocations || {}
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      const [pdvsRes, prizesRes] = await Promise.all([
        api.get('/admin/pdv', { params: { brand: BRAND } }),
        api.get('/admin/prizes', { params: { brand: BRAND } })
      ]);
      setPdvs(pdvsRes.data);
      setPrizes(prizesRes.data);
      if (!session) {
        const initialStock = {};
        prizesRes.data.forEach(p => { initialStock[p.id] = p.prize_type?.includes('try_again') ? 999 : 10; });
        setFormData(f => ({ ...f, stock_allocations: initialStock }));
      }
    };
    load().catch(console.error);
  }, [session]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const stock_allocations = Object.entries(formData.stock_allocations).map(([prizeId, quantity]) => ({
        prize_config_id: parseInt(prizeId),
        initial_quantity: parseInt(quantity)
      }));
      const payload = {
        name: formData.name, pdv_id: parseInt(formData.pdv_id),
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
        gain_mode: formData.gain_mode, ratio_value: parseInt(formData.ratio_value),
        stock_allocations, brand: BRAND
      };
      if (session) {
        await api.put(`/admin/sessions/${session.id}`, payload);
      } else {
        await api.post('/admin/sessions', payload);
      }
      await onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { color: '#1a3322', fontWeight: '700', fontSize: '13px', marginBottom: '6px', display: 'block' };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content" style={{ background: '#ffffff', border: `1px solid rgba(0,192,96,0.4)`, borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
          <div className="modal-header" style={{ borderBottom: `1px solid rgba(0,192,96,0.3)`, background: 'rgba(0,0,0,0.3)', borderRadius: '16px 16px 0 0' }}>
            <h5 className="modal-title fw-bold" style={{ color: '#1a3322' }}>
              <i className="fas fa-calendar-alt me-2" style={{ color: G }}></i>
              {session ? 'Modifier la session' : 'Nouvelle Session'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger mb-3">{error}</div>}
              <div className="row g-3">
                <div className="col-md-6">
                  <label style={labelStyle}>Nom de la session</label>
                  <input type="text" className="form-control" value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    required style={inputStyle} placeholder="Ex: Session Matin PDV Akwa" />
                </div>
                <div className="col-md-6">
                  <label style={labelStyle}>Point de Vente</label>
                  <select className="form-select" value={formData.pdv_id}
                    onChange={e => setFormData({ ...formData, pdv_id: e.target.value })}
                    required style={inputStyle}>
                    <option value="">Sélectionner un PDV</option>
                    {pdvs.map(pdv => <option key={pdv.id} value={pdv.id}>{pdv.name}</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label style={labelStyle}>Début</label>
                  <input type="datetime-local" className="form-control" value={formData.start_time}
                    onChange={e => setFormData({ ...formData, start_time: e.target.value })}
                    required style={inputStyle} />
                </div>
                <div className="col-md-6">
                  <label style={labelStyle}>Fin</label>
                  <input type="datetime-local" className="form-control" value={formData.end_time}
                    onChange={e => setFormData({ ...formData, end_time: e.target.value })}
                    required style={inputStyle} />
                </div>
                <div className="col-md-6">
                  <label style={labelStyle}>Mode de gain</label>
                  <select className="form-select" value={formData.gain_mode}
                    onChange={e => setFormData({ ...formData, gain_mode: e.target.value })}
                    style={inputStyle}>
                    <option value="auto">Automatique (ratio)</option>
                    <option value="PROBABILITY">Probabilité</option>
                    <option value="FORCED">Gagnant (100%)</option>
                    <option value="aleatoire">Aléatoire</option>
                  </select>
                </div>
                {formData.gain_mode === 'auto' && (
                <div className="col-md-6">
                  <label style={labelStyle}>Ratio (1 gagnant tous les X tours)</label>
                  <input type="number" className="form-control" value={formData.ratio_value}
                    onChange={e => setFormData({ ...formData, ratio_value: e.target.value })}
                    min="1" style={inputStyle} />
                </div>
                )}
              </div>

              {prizes.length > 0 && (
                <div className="mt-4">
                  <label style={labelStyle}>Stock initial par lot</label>
                  <div className="row g-2">
                    {prizes.map(prize => (
                      <div key={prize.id} className="col-md-4">
                        <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '10px', padding: '12px', border: `1px solid rgba(0,192,96,0.3)` }}>
                          <div style={{ color: '#00A651', fontWeight: 700, fontSize: '13px', marginBottom: '6px' }}>{prize.prize_label || prize.name}</div>
                          <input type="number" className="form-control form-control-sm"
                            value={formData.stock_allocations[prize.id] || 0}
                            onChange={e => setFormData({ ...formData, stock_allocations: { ...formData.stock_allocations, [prize.id]: parseInt(e.target.value) || 0 } })}
                            min="0" style={{ ...inputStyle, fontSize: '14px', fontWeight: 700 }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ borderTop: `1px solid rgba(0,192,96,0.3)` }}>
              <button type="button" className="btn fw-bold" onClick={onClose}
                style={{ background: 'rgba(0,0,0,0.06)', color: '#1a3322', border: '1px solid rgba(0,0,0,0.15)' }}>Annuler</button>
              <button type="submit" className="btn fw-bold" disabled={saving}
                style={{ background: `linear-gradient(135deg, ${G}, ${G_DARK})`, color: '#fff', border: 'none', padding: '8px 24px', boxShadow: '0 4px 14px rgba(0,192,96,0.3)' }}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Enregistrement...</> : <><i className="fas fa-save me-2"></i>{session ? 'Modifier' : 'Créer'}</>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Sessions;
